/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Redirect, Route } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import './App.css';
import { isAuthenticated } from '../../utils/auth';
import FadeSwitch from '../../containers/FadeSwitch';
import HomePage from '../../containers/HomePage';
import GamePage from '../../containers/GamePage';
import MobileGamePage from '../../containers/MobileGamePage';
import TournamentPage from '../../containers/TournamentPage';
import MobileTournamentPage from '../../containers/MobileTournamentPage';
import AuthPage from '../AuthPage';
import LoginPage from '../LoginPage';
import ProfilePage from '../ProfilePage';
import NotFoundPage from '../NotFoundPage';
import HeaderWithData from '../../containers/HeaderWithData';
import Footer from '../../components/Footer';
import { playSound, loopSound, stopLoopSound } from './actions';
import {
  TRANSITION_PAGE_OUT_SOUND,
  TRANSITION_PAGE_IN_SOUND,
  BACKGROUND_MUSIC_SOUND
} from '../../sounds';
import { makeSelectLocation, makeSelectCasinoSoundsOn } from './selectors';
import PrivateRoute from '../PrivateRoute';
import LogoutWithData from '../LogoutPage/LogoutWithData';
import Feed from '../../containers/Feed';
import FooterMobile from '../../components/FooterMobile';
import HeaderMobile from '../../components/HeaderMobile';
import { isMobile, isGamePage, parseQuery } from '../../utils/functions';
import ChangePasswordPage from '../ChangePasswordPage';
import RequestPasswordResetPage from 'containers/RequestPasswordResetPage';
import CompletePasswordResetPage from 'containers/CompletePasswordResetPage';
import DrawingDrumAlert from 'containers/DrawingDrumAlert';
import MobileGameListPage from '../MobileGameListPage';
import TournamentList from 'containers/TournamentList';
import {
  setMKeyCookie,
  getMKeyCookie,
  getCasinoSoundsCookie
} from '../../utils/cookies';
import MobileDetect from 'mobile-detect';
import { WINDCREEK_LEGACY_URL } from '../../utils/constants';

function shouldDarkenBackground({ pathname }) {
  return /^\/(tournament|game)/i.test(pathname);
}

function isLoggingOut({ pathname }) {
  return pathname === '/logout';
}

class App extends Component {
  constructor(props) {
    super(props);
    const { location } = props;

    this.storeMobileKey(location.search);

    this.state = { darkenBackground: false };

    this.handleRouteTransitionInterval = this.handleRouteTransitionInterval.bind(
      this
    );
  }

  componentWillReceiveProps(nextProps) {
    const authenticated = isAuthenticated();
    const {
      location,
      startBackgroundSounds,
      stopBackgroundSounds,
      casinoSoundsOn
    } = nextProps;
    const { casinoSoundsOn: prevSoundsOn } = this.props;

    this.storeMobileKey(location.search);

    if (authenticated) {
      if (
        location.pathname.includes('/game/') ||
        location.pathname.includes('/games/') ||
        location.pathname.includes('/tournament/') ||
        location.pathname.includes('/tournaments/') ||
        location.pathname.includes('/logout')
      ) {
        stopBackgroundSounds();
      } else {
        const soundCookie = getCasinoSoundsCookie();
        const cookieUndefined = typeof soundCookie === 'undefined';
        const soundOn = soundCookie === 'true';

        if (casinoSoundsOn && (soundOn || cookieUndefined)) {
          startBackgroundSounds();
        }
      }

      // checking prevSoundsOn ensures we don't dispatch this action
      // multiple times, which would cut off the fadeout.
      if (!casinoSoundsOn && prevSoundsOn) {
        stopBackgroundSounds();
      }
    }
  }

  storeMobileKey(key) {
    const params = parseQuery(key);

    if (
      params &&
      params.mkey &&
      params.mkey === process.env.REACT_APP_MOBILE_SECRET
    ) {
      setMKeyCookie(params.mkey);
    }
  }

  componentDidMount() {
    const darkenBackground = shouldDarkenBackground(this.props.location);

    if (darkenBackground !== this.state.darkenBackground) {
      this.setState({ darkenBackground });
    }
  }

  handleRouteTransitionInterval() {
    const { onRouteTransitionInterval, location } = this.props;

    const darkenBackground = shouldDarkenBackground(location);

    // Update the background opacity for new route.
    if (darkenBackground !== this.state.darkenBackground) {
      this.setState({ darkenBackground });
    }

    if (onRouteTransitionInterval) {
      onRouteTransitionInterval();
    }
  }

  getHeader() {
    const { location } = this.props;

    let header = null;
    if (!isLoggingOut(location) && isAuthenticated()) {
      if (!isMobile(location.pathname) && !getMKeyCookie()) {
        header = <HeaderWithData />;
      } else {
        if (!isGamePage(location.pathname)) {
          header = <HeaderMobile />;
        }
      }
    }

    return header;
  }

  getFooter() {
    const { location } = this.props;

    let footer = null;
    if (!isLoggingOut(location) && isAuthenticated()) {
      if (!isMobile(location.pathname) && !getMKeyCookie()) {
        footer = <Footer />;
      } else {
        if (!isGamePage(location.pathname)) {
          footer = <FooterMobile />;
        }
      }
    }

    return footer;
  }

  render() {
    const { onRouteTransitionStart, location } = this.props;
    const { darkenBackground } = this.state;

    // Check if user is authenticated.
    const authenticated = isAuthenticated();
    const isGaming = isGamePage(location.pathname);
    const isMobile = getMKeyCookie();

    // Redirect to logout if session invalid on legacy site.
    const params = parseQuery(location.search);
    if (authenticated) {
      if (params.from_legacy && !location.pathname.includes('/logout')) {
        return <Redirect to={'/logout'} />;
      }

      if (
        isMobile &&
        (location.pathname.includes('/members/home') ||
          location.pathname === '/')
      ) {
        return <Redirect to={'/mobile/games'} />;
      }
    }

    const mobileDetect = new MobileDetect(window.navigator.userAgent);
    if (
      !isMobile &&
      mobileDetect.mobile() &&
      !location.pathname.includes('resetPassword')
    ) {
      window.location = WINDCREEK_LEGACY_URL + '/download/app';
    }

    const mainClasses = [
      'main',
      isGaming ? 'gaming-main' : '',
      isMobile ? 'mobile-main' : '',
      darkenBackground ? 'main-dark' : ''
    ].join(' ');

    return (
      <div className="app">
        <Helmet
          titleTemplate="%s - Wind Creek Casino"
          defaultTitle="Wind Creek Casino"
        >
          <meta
            name="description"
            content="Welcome to Wind Creek Casino! Here, you can play online games for free, chat with other members, compete in online tournaments, and keep up with all the promotions and events happening at Wind Creek"
          />
        </Helmet>
        <div className="main-container">
          {this.getHeader()}
          <div className={mainClasses}>
            <FadeSwitch
              location={location}
              transitionProps={{ timeout: 250, classNames: 'fade' }}
              onTransitionStart={onRouteTransitionStart}
              onTransitionInterval={this.handleRouteTransitionInterval}
            >
              <Route exact path="/logout" component={LogoutWithData} />
              <Route
                exact
                path="/forgot-password"
                component={RequestPasswordResetPage}
              />
              <Route
                exact
                path="/resetPassword/:token"
                component={CompletePasswordResetPage}
              />
              <PrivateRoute
                condition={false}
                path="/login"
                exact
                component={LoginPage}
              />
              <PrivateRoute path="/game/:game" exact component={GamePage} />
              <PrivateRoute
                path="/mobile/games/play/:game"
                exact
                component={MobileGamePage}
              />
              <PrivateRoute
                path="/mobile/games"
                exact
                component={MobileGameListPage}
              />
              <PrivateRoute
                path="/tournament/:tournament"
                exact
                component={TournamentPage}
              />
              <PrivateRoute
                path="/mobile/tournaments/gametournament/:tournament"
                exact
                component={MobileTournamentPage}
              />
              <PrivateRoute
                path="/mobile/tournaments"
                exact
                component={TournamentList}
              />
              <PrivateRoute exact path="/mobile/feed" component={Feed} />
              <PrivateRoute
                path="/profile/change-password"
                exact
                component={ChangePasswordPage}
              />
              {/*<PrivateRoute path="/(profile|rewards)" component={ProfilePage} />*/}
              <PrivateRoute path="/rewards" component={ProfilePage} />
              {/* HomePage must be last route in Firewall section. */}
              {authenticated && (
                <PrivateRoute
                  key="HomePage"
                  path="/"
                  exact
                  component={HomePage}
                />
              )}
              {/* Show AuthPage at "/" path when not logged in. */}
              {!authenticated && (
                <Route key="AuthPage" path="/" exact component={AuthPage} />
              )}
              <Route path="*" component={NotFoundPage} />
            </FadeSwitch>
          </div>
          {this.getFooter()}
        </div>
        {authenticated && <DrawingDrumAlert />}
      </div>
    );
  }
}

App.propTypes = {
  location: PropTypes.shape({
    key: PropTypes.string,
    pathname: PropTypes.string
  }),
  onRouteTransitionStart: PropTypes.func,
  onRouteTransitionInterval: PropTypes.func,
  match: PropTypes.object,
  startBackgroundSounds: PropTypes.func,
  stopBackgroundSounds: PropTypes.func
};

function mapDispatchToProps(dispatch) {
  return {
    onRouteTransitionStart: () =>
      dispatch(playSound(TRANSITION_PAGE_OUT_SOUND)),
    onRouteTransitionInterval: () =>
      dispatch(playSound(TRANSITION_PAGE_IN_SOUND)),
    startBackgroundSounds: () => {
      dispatch(loopSound(BACKGROUND_MUSIC_SOUND));
    },
    stopBackgroundSounds: () => {
      dispatch(stopLoopSound(BACKGROUND_MUSIC_SOUND));
    }
  };
}

const mapStateToProps = createStructuredSelector({
  location: makeSelectLocation(),
  casinoSoundsOn: makeSelectCasinoSoundsOn()
});

export default connect(mapStateToProps, mapDispatchToProps)(App);



// WEBPACK FOOTER //
// ./src/containers/App/index.js